import { useState, useEffect, useCallback } from 'react';
import { siteImages, SiteImage } from '../mocks/siteImages';

const STORAGE_KEY = 'rockhound_site_images';

// Updated type to support both uploaded files and external URLs
interface ImageData {
  dataUrl?: string;
  externalUrl?: string;
  fileName?: string;
  uploadedAt?: string;
  fileSize?: number;
}

type SiteImageOverride = Record<string, ImageData>;

function getOverrides(): SiteImageOverride {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return {};
}

export function useSiteImages() {
  const [overrides, setOverrides] = useState<SiteImageOverride>(getOverrides);

  useEffect(() => {
    const handler = () => setOverrides(getOverrides());
    window.addEventListener('storage', handler);
    window.addEventListener('siteImagesUpdated', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('siteImagesUpdated', handler);
    };
  }, []);

  const getImageUrl = useCallback((imageId: string): string => {
    const override = overrides[imageId];
    if (override) {
      // Prioritize uploaded file (dataUrl) over external URL
      if (override.dataUrl) return override.dataUrl;
      if (override.externalUrl) return override.externalUrl;
    }
    const img = siteImages.find(i => i.id === imageId);
    return img ? img.defaultUrl : '';
  }, [overrides]);

  return { getImageUrl, overrides };
}

export function saveSiteImageOverrides(overrides: SiteImageOverride) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  window.dispatchEvent(new Event('siteImagesUpdated'));
}

export function getSiteImageUrl(imageId: string): string {
  const overrides = getOverrides();
  const override = overrides[imageId];
  if (override) {
    if (override.dataUrl) return override.dataUrl;
    if (override.externalUrl) return override.externalUrl;
  }
  const img = siteImages.find(i => i.id === imageId);
  return img ? img.defaultUrl : '';
}

export { siteImages };
export type { SiteImage, SiteImageOverride, ImageData };